import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import type { Snapshot, Settings } from "../shared/domain";
import { api, ApiError } from "./api";
import { detectTimezone } from "../shared/timezones";
interface State {
  data: Snapshot | null;
  loading: boolean;
  offline: boolean;
  connection: "checking" | "online" | "offline" | "unavailable";
  synced: number | null;
  error: string;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  clear: () => void;
}
const Store = createContext<State>(null!);
const keyName = "aethon-offline-key",
  dataName = "aethon-offline-data";
async function saveOffline(data: Snapshot) {
  let key = sessionStorage.getItem(keyName);
  if (!key) {
    const raw = crypto.getRandomValues(new Uint8Array(32));
    key = Array.from(raw)
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("");
    sessionStorage.setItem(keyName, key);
  }
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    Uint8Array.from(key.match(/../g)!.map((v) => parseInt(v, 16))),
    "AES-GCM",
    false,
    ["encrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    cryptoKey,
    new TextEncoder().encode(JSON.stringify({ data, synced: Date.now() })),
  );
  localStorage.setItem(
    dataName,
    JSON.stringify({
      iv: Array.from(iv),
      bytes: Array.from(new Uint8Array(encrypted)),
    }),
  );
}
async function loadOffline() {
  const key = sessionStorage.getItem(keyName),
    stored = localStorage.getItem(dataName);
  if (!key || !stored) return null;
  try {
    const { iv, bytes } = JSON.parse(stored);
    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      Uint8Array.from(key.match(/../g)!.map((v) => parseInt(v, 16))),
      "AES-GCM",
      false,
      ["decrypt"],
    );
    const raw = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: Uint8Array.from(iv) },
      cryptoKey,
      Uint8Array.from(bytes),
    );
    const value = JSON.parse(new TextDecoder().decode(raw)) as {
      data: Snapshot;
      synced: number;
    };
    return value.data.expiresAt > Date.now() ? value : null;
  } catch {
    return null;
  }
}
export function Provider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<Snapshot | null>(null),
    [loading, setLoading] = useState(true),
    [connection, setConnection] = useState<State["connection"]>(
      navigator.onLine ? "checking" : "offline",
    ),
    [synced, setSynced] = useState<number | null>(null),
    [error, setError] = useState("");
  const offline = connection !== "online";
  const inFlight = useRef<Promise<void> | null>(null);
  const clear = useCallback(() => {
    setData(null);
    setSynced(null);
    localStorage.removeItem(dataName);
    sessionStorage.removeItem(keyName);
  }, []);
  const refresh = useCallback(async () => {
    if (inFlight.current) return inFlight.current;
    const work = async () => {
      try {
        const next = await api<Snapshot>("/snapshot");
        if (!next.settings.timezoneConfigured) {
          next.settings = await api<Settings>(
            "/settings/initialize-timezone",
            "POST",
            { timezone: detectTimezone() },
          );
        }
        setData(next);
        setConnection("online");
        setSynced(Date.now());
        setError("");
        try {
          await saveOffline(next);
        } catch {
          setError(
            "Offline storage is unavailable in this browser session. Your server data is safe.",
          );
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          clear();
          setConnection("online");
        } else {
          setConnection(navigator.onLine ? "unavailable" : "offline");
          const cached = await loadOffline();
          if (cached) {
            setData(cached.data);
            setSynced(cached.synced);
          } else
            setError("Cannot connect. Connect to the local server to sign in.");
        }
      } finally {
        setLoading(false);
      }
    };
    inFlight.current = work();
    try {
      await inFlight.current;
    } finally {
      inFlight.current = null;
    }
  }, [clear]);
  useEffect(() => {
    void refresh();
    const online = () => {
      setConnection("checking");
      void refresh();
    };
    const off = () => setConnection("offline");
    const resume = () => {
      if (document.visibilityState === "visible" && navigator.onLine)
        void refresh();
    };
    const timer = setInterval(resume, 30000);
    window.addEventListener("online", online);
    window.addEventListener("offline", off);
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("session-expired", clear);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", off);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("session-expired", clear);
    };
  }, [refresh, clear]);
  useEffect(() => {
    if (!data) return;
    const timeout = setTimeout(clear, Math.max(0, data.expiresAt - Date.now()));
    return () => clearTimeout(timeout);
  }, [data, clear]);
  useEffect(() => {
    const theme = data?.settings.theme || "dark";
    const media = matchMedia("(prefers-color-scheme: dark)");
    const apply = () =>
      (document.documentElement.dataset.theme =
        theme === "system" ? (media.matches ? "dark" : "light") : theme);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [data?.settings.theme]);
  const signOut = async () => {
    await api("/logout", "POST", {});
    clear();
  };
  return (
    <Store.Provider
      value={{
        data,
        loading,
        offline,
        connection,
        synced,
        error,
        refresh,
        signOut,
        clear,
      }}
    >
      {children}
    </Store.Provider>
  );
}
export const useStore = () => useContext(Store);
